import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnChanges,
  output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { TranslatePipe } from '@ngx-translate/core';
import { IconComponent } from '../icon/icon.component';
import { IconName } from '@app/utils/enums/icon-enum';
import { IDateRangeFilterSelection, IFilterOptions } from '@app/utils/interfaces/orders.interface';
import { DateRanges } from '@app/utils/enums/date-range-filter.enum';

@Component({
  selector: 'app-date-range-filter',
  standalone: true,
  imports: [
    TranslatePipe,
    MatFormFieldModule,
    MatDatepickerModule,
    ReactiveFormsModule,
    DatePipe,
    IconComponent,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './date-range-filter.component.html',
  styleUrl: './date-range-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeFilterComponent implements OnChanges {
  public readonly dateRangeOptions = input.required<IFilterOptions[]>();
  public readonly externalSelectedOption = input<IDateRangeFilterSelection | null>(null);
  public readonly toggleFilterOptions = output<IDateRangeFilterSelection>();

  public readonly dateRangeFilters = DateRanges;
  public readonly selectedOption = signal<string | null>(null);
  public readonly dateRangeFormat = 'yyyy/MM/dd';
  public readonly IconName = IconName;

  public readonly rangeForm = new FormGroup({
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null),
  });

  public ngOnChanges(): void {
    if (!this.externalSelectedOption() && this.selectedOption() !== null) {
      this.resetFilter();
    }
  }

  public toggleOption(optionKey: string, isSelected: boolean): void {
    if (!isSelected) {
      this.resetFilter();
      return;
    }

    this.applyPredefinedRange(optionKey);
    this.selectedOption.set(optionKey);
    this.emitAndSync();
  }

  public onPickerClosed(): void {
    const { startDate, endDate } = this.rangeForm.value;
    if (startDate && endDate) {
      this.selectedOption.set(this.dateRangeFilters.Custom);
      this.emitAndSync();
    } else {
      this.resetFilter();
    }
  }

  public cancelDatePicker(event: Event): void {
    event.stopPropagation();
    this.resetFilter();
  }

  private resetFilter(): void {
    this.selectedOption.set(null);
    this.rangeForm.reset();
    this.toggleFilterOptions.emit({
      optionKey: this.selectedOption(),
      startDate: null,
      endDate: null,
    });
  }

  private emitAndSync(): void {
    const { startDate, endDate } = this.rangeForm.value;
    if (startDate && endDate) {
      this.toggleFilterOptions.emit({
        optionKey: this.selectedOption(),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
    }
  }

  private applyPredefinedRange(optionKey: string): void {
    const start = new Date();
    const end = new Date();

    switch (optionKey) {
      case DateRanges.Today:
        break;

      case DateRanges.Yesterday:
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() - 1);
        break;

      case DateRanges.Last7days:
        start.setDate(start.getDate() - 7);
        break;

      case DateRanges.Last30days:
        start.setDate(start.getDate() - 30);
        break;

      case DateRanges.ThisMonth:
        start.setDate(1);
        break;

      case DateRanges.LastMonth:
        start.setMonth(start.getMonth() - 1, 1);
        end.setDate(0);
        break;

      default:
        this.rangeForm.reset();
        return;
    }

    this.rangeForm.patchValue({
      startDate: this.getStartOfDay(start),
      endDate: this.getEndOfDay(end),
    });
  }

  private getStartOfDay(date: Date): Date {
    return new Date(date.setHours(0, 0, 0, 0));
  }

  private getEndOfDay(date: Date): Date {
    return new Date(date.setHours(23, 59, 59, 999));
  }
}
